import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import { ticketService } from '@/services/ticketService';
import { queryKeys } from '@/lib/queryKeys';
import { useTenantScope } from './useTenantScope';
import type {
  CreateCommentoInput,
  CreateTicketInput,
  Operatore,
  Paginated,
  SlaPolicy,
  TicketAllegato,
  TicketCategoria,
  TicketCommento,
  TicketConRelazioni,
  TicketFilters,
  UUID,
  UpdateTicketInput,
  Utente,
} from '@/types';
import { categorieService } from '@/services/categorieService';

export function useTickets(
  filters: TicketFilters & { ignoreTenantScope?: boolean; enabled?: boolean } = {},
): UseQueryResult<Paginated<TicketConRelazioni>> {
  const { ignoreTenantScope = false, enabled = true, ...rest } = filters;
  const clienteId = useTenantScope();
  const merged: TicketFilters = {
    ...rest,
    clienteId: ignoreTenantScope ? rest.clienteId : (rest.clienteId ?? clienteId),
  };

  return useQuery({
    queryKey: queryKeys.tickets.list(merged),
    queryFn: () => ticketService.list(merged),
    enabled,
    meta: { resourceName: 'ticket' },
  });
}

export function useTicket(id: UUID | undefined): UseQueryResult<TicketConRelazioni> {
  return useQuery({
    queryKey: queryKeys.tickets.detail(id ?? ''),
    queryFn: () => ticketService.get(id as UUID),
    enabled: Boolean(id),
    meta: { resourceName: 'ticket' },
  });
}

export function useTicketCommenti(id: UUID | undefined): UseQueryResult<TicketCommento[]> {
  return useQuery({
    queryKey: queryKeys.tickets.commenti(id ?? ''),
    queryFn: () => ticketService.listCommenti(id as UUID),
    enabled: Boolean(id),
  });
}

export function useTicketAllegati(id: UUID | undefined): UseQueryResult<TicketAllegato[]> {
  return useQuery({
    queryKey: queryKeys.tickets.allegati(id ?? ''),
    queryFn: () => ticketService.listAllegati(id as UUID),
    enabled: Boolean(id),
  });
}

export function useTicketCategorie(includeInactive = false): UseQueryResult<TicketCategoria[]> {
  return useQuery({
    queryKey: [...queryKeys.tickets.categorie, includeInactive ? 'all' : 'active'] as const,
    queryFn: () => categorieService.list(includeInactive),
    staleTime: 60 * 60_000,
  });
}

export function useSlaPolicies(enabled = true): UseQueryResult<SlaPolicy[]> {
  return useQuery({
    queryKey: queryKeys.tickets.slaPolicies,
    queryFn: () => ticketService.listSlaPolicies(),
    enabled,
    staleTime: 60 * 60_000,
  });
}

export function useAssignees(categoriaId?: UUID | null, enabled = true): UseQueryResult<Operatore[]> {
  return useQuery({
    queryKey: queryKeys.utenti.operatori(categoriaId ?? undefined),
    queryFn: () => ticketService.listAssignees(categoriaId ?? undefined),
    enabled: enabled && Boolean(categoriaId),
    staleTime: 10 * 60_000,
  });
}

export function useAllAssignees(enabled = true): UseQueryResult<Operatore[]> {
  return useQuery({
    queryKey: queryKeys.utenti.operatori(),
    queryFn: () => ticketService.listAssignees(),
    enabled,
    staleTime: 10 * 60_000,
  });
}

export function useStakeholderCandidates(
  params: { search?: string; clienteId?: UUID; enabled?: boolean } = {},
): UseQueryResult<Utente[]> {
  const { enabled = true, ...filters } = params;

  return useQuery({
    queryKey: queryKeys.tickets.stakeholderCandidates(filters),
    queryFn: () => ticketService.listStakeholderCandidates(filters),
    enabled,
    staleTime: 30_000,
  });
}

export function useCreateTicket(): UseMutationResult<TicketConRelazioni, Error, CreateTicketInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateTicketInput) => ticketService.create(input),
    // Field errors are mapped onto the form, so no global toast for failures.
    meta: { successMessage: 'Ticket creato con successo.', silentError: true },
    onSuccess: (ticket) => {
      queryClient.setQueryData(queryKeys.tickets.detail(ticket.id), ticket);
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

interface UpdateTicketVariables {
  id: UUID;
  input: UpdateTicketInput;
}

/**
 * Optimistic ticket update.
 *
 * Status and priority changes are the most frequent interaction in the queue,
 * and a round-trip before the badge changes makes the UI feel broken. We patch
 * the detail entry and every cached list page, snapshot the previous state, and
 * roll back on error. Lists are then invalidated on settle because a status
 * change can move a row out of the active filter.
 */
export function useUpdateTicket(): UseMutationResult<
  TicketConRelazioni,
  Error,
  UpdateTicketVariables,
  { previousDetail?: TicketConRelazioni }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, input }: UpdateTicketVariables) => ticketService.update(id, input),
    meta: { successMessage: 'Ticket aggiornato.' },

    onMutate: async ({ id, input }) => {
      const detailKey = queryKeys.tickets.detail(id);
      await queryClient.cancelQueries({ queryKey: detailKey });

      const previousDetail = queryClient.getQueryData<TicketConRelazioni>(detailKey);
      if (previousDetail) {
        queryClient.setQueryData<TicketConRelazioni>(detailKey, { ...previousDetail, ...input });
      }

      // Patch the row inside every cached list so the table reflects the change
      // without waiting for a refetch.
      queryClient.setQueriesData<Paginated<TicketConRelazioni>>(
        { queryKey: [...queryKeys.tickets.all, 'list'] },
        (page) => {
          if (!page) return page;
          return {
            ...page,
            data: page.data.map((row) => (row.id === id ? { ...row, ...input } : row)),
          };
        },
      );

      return { previousDetail };
    },

    onError: (_error, { id }, context) => {
      if (context?.previousDetail) {
        queryClient.setQueryData(queryKeys.tickets.detail(id), context.previousDetail);
      }
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
    },

    onSuccess: (ticket) => {
      queryClient.setQueryData(queryKeys.tickets.detail(ticket.id), ticket);
    },

    onSettled: (_data, _error, variables) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
      const stato = variables?.input.stato;
      if (stato === 'risolto' || stato === 'chiuso') {
        void queryClient.invalidateQueries({ queryKey: queryKeys.cedolini.all });
      }
    },
  });
}

export function useAssignTicket(): UseMutationResult<TicketConRelazioni, Error, { id: UUID; utenteId: UUID | null }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, utenteId }: { id: UUID; utenteId: UUID | null }) => ticketService.assign(id, utenteId),
    meta: { successMessage: 'Assegnazione aggiornata.' },
    onSuccess: (ticket) => {
      queryClient.setQueryData(queryKeys.tickets.detail(ticket.id), ticket);
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
    },
  });
}

export function useTicketCestino(
  filters: TicketFilters & { enabled?: boolean } = {},
): UseQueryResult<Paginated<TicketConRelazioni>> {
  const { enabled = true, ...rest } = filters;
  return useQuery({
    queryKey: queryKeys.tickets.cestino(rest),
    queryFn: () => ticketService.cestino(rest),
    enabled,
    meta: { resourceName: 'cestino ticket' },
  });
}

export function useDeleteTicket(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => ticketService.remove(id),
    meta: { successMessage: 'Ticket spostato nel cestino.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useRestoreTicket(): UseMutationResult<TicketConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => ticketService.restore(id),
    meta: { successMessage: 'Ticket ripristinato.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
    },
  });
}

export function usePurgeTicket(): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: UUID) => ticketService.purge(id),
    meta: { successMessage: 'Ticket eliminato definitivamente.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
    },
  });
}

export function useReopenTicket(): UseMutationResult<TicketConRelazioni, Error, UUID> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: UUID) => ticketService.reopen(id),
    meta: { successMessage: 'Ticket riaperto.' },
    onSuccess: (ticket) => {
      queryClient.setQueryData(queryKeys.tickets.detail(ticket.id), ticket);
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.all });
      void queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all });
    },
  });
}

export function useAddCommento(
  ticketId: UUID,
): UseMutationResult<TicketCommento, Error, CreateCommentoInput> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreateCommentoInput) => ticketService.addCommento(ticketId, input),
    onSuccess: (commento) => {
      // Append instead of invalidating: the thread is ordered and we already
      // have the authoritative row, so a refetch would only add latency.
      queryClient.setQueryData<TicketCommento[]>(queryKeys.tickets.commenti(ticketId), (previous) =>
        previous ? [...previous, commento] : [commento],
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.detail(ticketId) });
    },
  });
}

export function useUploadAllegato(
  ticketId: UUID,
): UseMutationResult<TicketAllegato, Error, { file: File; onProgress?: (percent: number) => void }> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ file, onProgress }: { file: File; onProgress?: (percent: number) => void }) =>
      ticketService.uploadAllegato(ticketId, file, onProgress),
    meta: { successMessage: 'Allegato caricato.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.allegati(ticketId) });
    },
  });
}

export function useDeleteAllegato(ticketId: UUID): UseMutationResult<void, Error, UUID> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (allegatoId: UUID) => ticketService.deleteAllegato(ticketId, allegatoId),
    meta: { successMessage: 'Allegato rimosso.' },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.tickets.allegati(ticketId) });
    },
  });
}
